import React, { useState } from "react";
import { CharCount } from "../CharCount";
import { Flame, Target } from "lucide-react";
import { useAutosave } from "../../lib/useAutosave";
import "../../styles/MyCraftzPage.css";

import { siddhiText, LANG_LABEL } from "../../lib/siddhiText";

function evidenceLine(ev, t) {
  const parts = [t.daysKept(ev.days_kept, ev.target), t.longest(ev.longest_run)];
  if (ev.misses === 0) parts.push(t.noMiss);
  else if (ev.recoveries === ev.misses) parts.push(ev.misses === 1 ? t.oneRecovered : t.allRecovered(ev.misses));
  else parts.push(t.someRecovered(ev.misses, ev.recoveries));
  return parts.join(" · ");
}

function groupByText(refl, key) {
  const groups = [];
  refl.forEach((r) => {
    const text = (r[key] || "").trim();
    if (!text) return;
    const g = groups.find((x) => x.text.toLowerCase() === text.toLowerCase());
    if (g) g.names.push(r.title);
    else groups.push({ text, names: [r.title] });
  });
  return groups;
}

function RetroBlock({ label, guessLead, guesses, actualLead, actual, testId, t, raw = false }) {
  return (
    <div className="adp-retro-block" data-testid={testId}>
      <p className="adp-retro-label">{label}</p>
      <p className="adp-retro-guess">{guessLead}</p>
      {guesses.length === 0 ? (
        <p className="adp-retro-guess">&mdash;</p>
      ) : (
        <ul className="real-retro-guesses" data-testid={`${testId}-guesses`}>
          {guesses.map((g, i) => (
            <li key={i} data-testid={`${testId}-guess-${i}`}>
              <span className="real-retro-practice">{g.names.length > 1 ? `${g.names.slice(0, -1).join(", ")} ${t.and} ${g.names[g.names.length - 1]}` : g.names[0]}</span> &ldquo;{g.text}&rdquo;
            </li>
          ))}
        </ul>
      )}
      {actual ? (
        <p className="adp-retro-actual" data-testid={`${testId}-actual`}>{raw || /^what\b/i.test(actual.trim()) ? actual : `${actualLead} ${actual}`}</p>
      ) : (
        <p className="adp-retro-actual adp-retro-pending" data-testid={`${testId}-pending`}>
          {t.pending}
        </p>
      )}
    </div>
  );
}

function IdentityBox({ value, onSave, t }) {
  const [draft, setDraft] = useState(value || "");
  React.useEffect(() => { setDraft(value || ""); }, [value]);
  const { status, flush } = useAutosave({ draft, savedValue: value, onSave });
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft]);
  return (
    <div className="adp-identity" data-testid="siddhi-identity-block">
      <p className="adp-retro-label">{t.identity}</p>
      <textarea
        ref={ref}
        className="adp-identity-input"
        style={{ overflow: "hidden", resize: "none" }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={flush}
        rows={2}
        maxLength={600}
        data-testid="siddhi-identity-textarea"
        aria-label="Your identity statement"
      />
      <CharCount value={draft} max={600} testId="siddhi-identity-count" />
      <p className="adp-identity-status" aria-live="polite" data-testid="siddhi-identity-status">
        {status === "saving" ? t.saving : status === "saved" ? t.saved : status === "error" ? t.saveErr : ""}
      </p>
    </div>
  );
}

/* Real Siddhi in the demo's "Siddhi — your retrospective" structure
   (AgedDemoPreview month panel), filled only with the user's real data. */
export function RealSiddhiRetro({ siddhi, onSaveIdentity, lang }) {
  const t = siddhiText(lang);
  const ev = siddhi.evidence;
  const refl = siddhi.reflections || [];
  const day = ev ? Math.min(ev.days_since_sealing, ev.target) : 0;

  return (
    <div className="adp-panel real-siddhi" data-testid="siddhi-retro">
      {ev && (
        <div className="adp-sadhana" data-testid="siddhi-stat-card">
          <div className="adp-streak-row">
            <span className="adp-streak" data-testid="siddhi-days-kept"><Flame size={15} strokeWidth={1.8} /> {t.daysKept(ev.days_kept, ev.target)}</span>
            <span className={`adp-daytag${day >= ev.target ? " adp-daytag-done" : ""}`} data-testid="siddhi-day-tag">
              {day >= ev.target ? t.cycleDone(ev.target) : t.dayTag(day, ev.target)}
            </span>
          </div>
          <div className="adp-strip" data-testid="siddhi-daystrip">
            {(ev.strip || []).map((st, i) => (
              <span key={i} className={`adp-dot ${st === "kept" ? "is-kept" : st === "miss" ? "is-miss" : st === "today" ? "is-today" : "is-future"}`} title={t.dot(i + 1, st)} />
            ))}
          </div>
          <p className="adp-practice">{t.practice} {refl.map((r) => r.title).filter(Boolean).join(" · ")}</p>
        </div>
      )}

      <div className="adp-siddhi" data-testid="siddhi-month">
        <p className="adp-siddhi-head"><Target size={13} strokeWidth={1.8} /> {t.head}</p>
        <RetroBlock
          label={t.phala} guessLead={t.guessPhala} guesses={groupByText(refl, "phala")} t={t}
          actualLead={t.arrived} actual={siddhi.woop?.arrived} testId="siddhi-retro-phala"
        />
        <RetroBlock
          label={t.vighna} guessLead={t.guessVighna} guesses={groupByText(refl, "vighna")} t={t}
          actualLead={t.inTheWay} actual={siddhi.woop?.in_the_way} testId="siddhi-retro-vighna" raw
        />
        {ev && (
          <div className="adp-evidence" data-testid="siddhi-evidence">
            <Flame size={12} strokeWidth={1.8} /> {evidenceLine(ev, t)}
          </div>
        )}
        <IdentityBox value={siddhi.identity_statement} onSave={onSaveIdentity} t={t} />
      </div>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export function EarlierVersions({ versions, lang }) {
  const t = siddhiText(lang);
  const older = (versions || []).slice(1);
  if (older.length === 0) return null;
  return (
    <details className="adp-panel real-siddhi-versions" data-testid="siddhi-earlier-versions">
      <summary className="adp-retro-label">{t.earlier(older.length)}</summary>
      {older.map((v) => (
        <div key={v.version} className="adp-retro-block" data-testid={`siddhi-version-${v.version}`}>
          <p className="adp-retro-label" data-testid={`siddhi-version-${v.version}-label`}>{t.version(v.version)} · {LANG_LABEL[v.lang] || LANG_LABEL.en} · {fmt(v.generated_at)}</p>
          {v.note ? <p className="adp-retro-guess">{t.note} &ldquo;{v.note}&rdquo;</p> : null}
          <p className="adp-retro-actual"><em>{v.identity_statement}</em></p>
          <p className="adp-retro-actual">{v.woop?.wish}</p>
        </div>
      ))}
    </details>
  );
}

/* Turn a Siddhi into a normal Chapter, pre-filled with the Identity line. */
export function SealAsChapter({ book, identity, user, authHeaders }) {
  const [state, setState] = useState("idle"); // idle | saving | done | error
  const [text, setText] = useState(identity || "");
  React.useEffect(() => { setText(identity || ""); }, [identity]);
  const seal = async () => {
    setState("saving");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapters-within`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          book_title: book.title, book_author: book.author || undefined, reflection: text.trim(),
          name: user?.name || "Reader", email: user?.email, language: "en",
        }),
      });
      setState(r.ok ? "done" : "error");
    } catch (e) {
      setState("error");
    }
  };
  return (
    <div className="adp-panel real-siddhi" data-testid="siddhi-seal-chapter">
      <p className="adp-retro-label">Seal this as a chapter</p>
      <p className="adp-retro-guess">A Chapter is what moved you; this one records who you became by practising it.</p>
      {state === "done" ? (
        <p className="adp-retro-actual" data-testid="siddhi-seal-chapter-done">Sealed into your story ✓ — see it in My Sanctuary.</p>
      ) : (
        <>
          <textarea className="adp-identity-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="siddhi-seal-chapter-text" />
          <button type="button" className="dcp-btn-save" onClick={seal} disabled={state === "saving" || text.trim().length < 2} data-testid="siddhi-seal-chapter-btn">
            {state === "saving" ? "Sealing…" : "Seal as a chapter"}
          </button>
          {state === "error" && <p className="sad-field-error" role="alert">Couldn&apos;t seal it. Please try again.</p>}
        </>
      )}
    </div>
  );
}
